import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

/* ─── TOOL CATEGORIES ───────────────────────────────────────────────── */
const CATEGORIES = [
  {
    id:       'network',
    label:    'Network',
    icon:     '🌐',
    color:    '#00c8f0',
    glow:     'rgba(0,200,240,.18)',
    path:     '/tools/ip',
    desc:     'IP lookup, traceroute, DNS, port scan, blacklist check and more',
    tools: [
      { name:'What Is My IP',   icon:'📡' },
      { name:'IP Lookup',       icon:'🔍' },
      { name:'DNS Lookup',      icon:'🌍' },
      { name:'Port Scanner',    icon:'🔌' },
      { name:'Traceroute',      icon:'🗺️' },
      { name:'WHOIS',           icon:'📋' },
      { name:'Blacklist Check', icon:'🛡️' },
      { name:'Speed Test',      icon:'⚡' },
    ],
    badge: '10 tools',
  },
  {
    id:       'developer',
    label:    'Developer',
    icon:     '⌨️',
    color:    '#b78cff',
    glow:     'rgba(183,140,255,.18)',
    path:     '/tools/dev',
    desc:     'JSON formatter, validator, text diff — no data leaves your browser',
    tools: [
      { name:'JSON Formatter', icon:'{ }' },
      { name:'JSON Validator', icon:'✓'   },
      { name:'JSON Minifier',  icon:'⬇'   },
      { name:'Text Diff',      icon:'±'   },
    ],
    badge: '4 tools',
  },
  {
    id:       'files',
    label:    'File Tools',
    icon:     '🗂️',
    color:    '#3dd68c',
    glow:     'rgba(61,214,140,.18)',
    path:     '/tools/files',
    desc:     'Convert images, merge PDFs, compress PDFs — fast and private',
    tools: [
      { name:'Image Converter', icon:'🖼️' },
      { name:'PDF Merger',      icon:'📑' },
      { name:'PDF Compressor',  icon:'🗜️' },
    ],
    badge: '3 tools',
  },
];

/* ─── STATS ─────────────────────────────────────────────────────────── */
const STATS = [
  { value:'17+',  label:'Free tools'        },
  { value:'100%', label:'Browser-side where possible' },
  { value:'0',    label:'Sign-ups required' },
  { value:'Free', label:'Always'            },
];

/* ─── FLOATING GRID BACKGROUND ──────────────────────────────────────── */
function GridBg() {
  return (
    <div style={{
      position:'fixed', inset:0, zIndex:0, pointerEvents:'none',
      overflow:'hidden',
    }}>
      {/* grid lines */}
      <div style={{
        position:'absolute', inset:0,
        backgroundImage:`
          linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,.025) 1px, transparent 1px)
        `,
        backgroundSize:'60px 60px',
      }}/>
      {/* radial gradient overlay */}
      <div style={{
        position:'absolute', inset:0,
        background:'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(0,200,240,.06) 0%, transparent 70%)',
      }}/>
      {/* corner glow */}
      <div style={{
        position:'absolute', top:-200, right:-200,
        width:600, height:600, borderRadius:'50%',
        background:'radial-gradient(circle, rgba(183,140,255,.06) 0%, transparent 70%)',
      }}/>
      <div style={{
        position:'absolute', bottom:-200, left:-200,
        width:500, height:500, borderRadius:'50%',
        background:'radial-gradient(circle, rgba(61,214,140,.05) 0%, transparent 70%)',
      }}/>
    </div>
  );
}

/* ─── TOOL CHIP ──────────────────────────────────────────────────────── */
function ToolChip({ icon, name, color }) {
  return (
    <div style={{
      display:'inline-flex', alignItems:'center', gap:5,
      padding:'4px 9px',
      borderRadius:6,
      background:`${color}10`,
      border:`1px solid ${color}22`,
      fontSize:11.5,
      color:`${color}cc`,
      whiteSpace:'nowrap',
    }}>
      <span style={{ fontSize:11 }}>{icon}</span>
      {name}
    </div>
  );
}

/* ─── CATEGORY CARD ──────────────────────────────────────────────────── */
function CategoryCard({ cat, index }) {
  const [hovered, setHovered] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), index * 120);
    return () => clearTimeout(t);
  }, [index]);

  return (
    <Link
      to={cat.path}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        textDecoration:'none',
        display:'block',
        opacity:   mounted ? 1 : 0,
        transform: mounted ? 'translateY(0)' : 'translateY(24px)',
        transition:'opacity .5s ease, transform .5s ease',
      }}
    >
      <div style={{
        position:'relative',
        background: hovered
          ? `linear-gradient(135deg, rgba(255,255,255,.04), rgba(255,255,255,.02))`
          : 'rgba(255,255,255,.025)',
        border:`1px solid ${hovered ? cat.color+'45' : 'rgba(255,255,255,.07)'}`,
        borderRadius:16,
        padding:'28px 26px',
        overflow:'hidden',
        transition:'all .22s ease',
        cursor:'pointer',
        boxShadow: hovered ? `0 8px 40px ${cat.glow}, 0 0 0 1px ${cat.color}20` : 'none',
      }}>

        {/* top glow on hover */}
        <div style={{
          position:'absolute', top:0, left:0, right:0, height:1,
          background: hovered
            ? `linear-gradient(90deg, transparent, ${cat.color}60, transparent)`
            : 'transparent',
          transition:'all .22s',
        }}/>

        {/* corner accent */}
        <div style={{
          position:'absolute', top:-30, right:-30,
          width:120, height:120, borderRadius:'50%',
          background:`radial-gradient(circle, ${cat.color}${hovered?'12':'08'} 0%, transparent 70%)`,
          transition:'all .22s',
        }}/>

        {/* header */}
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{
              width:46, height:46, borderRadius:12,
              background:`${cat.color}15`,
              border:`1px solid ${cat.color}30`,
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:22,
              boxShadow: hovered ? `0 0 20px ${cat.color}25` : 'none',
              transition:'box-shadow .22s',
            }}>{cat.icon}</div>
            <div>
              <div style={{
                fontSize:18, fontWeight:800,
                color:'rgba(221,229,245,.95)',
                fontFamily:'"Syne","DM Sans",system-ui,sans-serif',
                letterSpacing:'-.01em',
              }}>{cat.label}</div>
              <div style={{
                fontSize:11, fontWeight:600,
                color:`${cat.color}`,
                letterSpacing:'.08em', textTransform:'uppercase',
                marginTop:1,
              }}>{cat.badge}</div>
            </div>
          </div>

          {/* arrow */}
          <div style={{
            width:30, height:30, borderRadius:8,
            background:`${cat.color}12`,
            border:`1px solid ${cat.color}25`,
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:13, color:cat.color,
            transform: hovered ? 'translate(2px,-2px)' : 'none',
            transition:'transform .18s',
          }}>→</div>
        </div>

        {/* description */}
        <p style={{
          fontSize:13, color:'rgba(221,229,245,.5)',
          lineHeight:1.65, marginBottom:18,
          fontFamily:'"DM Sans",system-ui,sans-serif',
        }}>{cat.desc}</p>

        {/* tool chips */}
        <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
          {cat.tools.map(t => (
            <ToolChip key={t.name} icon={t.icon} name={t.name} color={cat.color}/>
          ))}
        </div>
      </div>
    </Link>
  );
}

/* ─── STAT ITEM ──────────────────────────────────────────────────────── */
function StatItem({ value, label, index }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 300 + index * 80);
    return () => clearTimeout(t);
  }, [index]);

  return (
    <div style={{
      textAlign:'center',
      opacity:   mounted ? 1 : 0,
      transform: mounted ? 'translateY(0)' : 'translateY(12px)',
      transition:'opacity .45s ease, transform .45s ease',
    }}>
      <div style={{
        fontSize:'clamp(1.8rem,3.5vw,2.4rem)',
        fontWeight:900,
        background:'linear-gradient(135deg, #fff 30%, rgba(0,200,240,.8))',
        WebkitBackgroundClip:'text',
        WebkitTextFillColor:'transparent',
        backgroundClip:'text',
        fontFamily:'"Syne",system-ui,sans-serif',
        letterSpacing:'-.03em',
        lineHeight:1,
        marginBottom:6,
      }}>{value}</div>
      <div style={{
        fontSize:12,
        color:'rgba(221,229,245,.38)',
        fontFamily:'"DM Sans",system-ui,sans-serif',
        letterSpacing:'.02em',
      }}>{label}</div>
    </div>
  );
}

/* ─── MAIN PAGE ──────────────────────────────────────────────────────── */
export default function ToolsLanding() {
  const [headingMounted, setHeadingMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setHeadingMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Sans:wght@400;500;600&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        a { text-decoration:none; }
      `}</style>

      <div style={{
        minHeight:'100vh',
        background:'#07080c',
        fontFamily:'"DM Sans",system-ui,sans-serif',
        position:'relative',
        overflow:'hidden',
      }}>
        <GridBg/>

        <div style={{ position:'relative', zIndex:1, padding:'0 0 64px' }}>

          {/* ── HERO ───────────────────────────────────────────────── */}
          <div style={{
            textAlign:'center',
            padding:'72px 24px 56px',
            maxWidth:680,
            margin:'0 auto',
          }}>
            {/* eyebrow */}
            <div style={{
              display:'inline-flex', alignItems:'center', gap:7,
              padding:'5px 14px',
              borderRadius:20,
              background:'rgba(0,200,240,.08)',
              border:'1px solid rgba(0,200,240,.2)',
              fontSize:11.5, fontWeight:700,
              color:'rgba(0,200,240,.9)',
              letterSpacing:'.1em', textTransform:'uppercase',
              marginBottom:24,
              opacity:   headingMounted ? 1 : 0,
              transform: headingMounted ? 'translateY(0)' : 'translateY(-10px)',
              transition:'opacity .5s ease, transform .5s ease',
            }}>
              <span style={{
                width:6, height:6, borderRadius:'50%',
                background:'#00c8f0',
                boxShadow:'0 0 8px #00c8f0',
                display:'inline-block',
              }}/>
              Free Tools by Arhatinfo
            </div>

            {/* heading */}
            <h1 style={{
              fontSize:'clamp(2.2rem,5.5vw,3.6rem)',
              fontWeight:900,
              lineHeight:1.08,
              letterSpacing:'-.03em',
              fontFamily:'"Syne",system-ui,sans-serif',
              marginBottom:18,
              opacity:   headingMounted ? 1 : 0,
              transform: headingMounted ? 'translateY(0)' : 'translateY(16px)',
              transition:'opacity .55s ease .08s, transform .55s ease .08s',
            }}>
              <span style={{ color:'rgba(221,229,245,.95)' }}>
                Developer tools
              </span>
              <br/>
              <span style={{
                background:'linear-gradient(90deg, #00c8f0, #b78cff 50%, #3dd68c)',
                WebkitBackgroundClip:'text',
                WebkitTextFillColor:'transparent',
                backgroundClip:'text',
              }}>
                that actually work
              </span>
            </h1>

            {/* subheading */}
            <p style={{
              fontSize:'clamp(14px,2vw,16px)',
              color:'rgba(221,229,245,.48)',
              lineHeight:1.75,
              maxWidth:500,
              margin:'0 auto 32px',
              opacity:   headingMounted ? 1 : 0,
              transform: headingMounted ? 'translateY(0)' : 'translateY(12px)',
              transition:'opacity .55s ease .16s, transform .55s ease .16s',
            }}>
              Network diagnostics, developer utilities, and file processing tools.
              No sign-up. No ads. No nonsense.
            </p>

            {/* CTA */}
            <div style={{
              display:'flex', justifyContent:'center', gap:10, flexWrap:'wrap',
              opacity:   headingMounted ? 1 : 0,
              transform: headingMounted ? 'translateY(0)' : 'translateY(10px)',
              transition:'opacity .55s ease .24s, transform .55s ease .24s',
            }}>
              <Link to="/tools/ip" style={{
                padding:'11px 24px',
                borderRadius:9,
                background:'rgba(0,200,240,.12)',
                border:'1px solid rgba(0,200,240,.35)',
                color:'#00c8f0',
                fontSize:13.5, fontWeight:700,
                fontFamily:'"Syne",system-ui,sans-serif',
                display:'inline-flex', alignItems:'center', gap:7,
                transition:'all .16s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background='rgba(0,200,240,.2)'; }}
              onMouseLeave={e => { e.currentTarget.style.background='rgba(0,200,240,.12)'; }}
              >
                🌐 Network Tools
              </Link>
              <Link to="/tools/dev" style={{
                padding:'11px 24px',
                borderRadius:9,
                background:'rgba(183,140,255,.1)',
                border:'1px solid rgba(183,140,255,.3)',
                color:'#b78cff',
                fontSize:13.5, fontWeight:700,
                fontFamily:'"Syne",system-ui,sans-serif',
                display:'inline-flex', alignItems:'center', gap:7,
                transition:'all .16s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background='rgba(183,140,255,.18)'; }}
              onMouseLeave={e => { e.currentTarget.style.background='rgba(183,140,255,.1)'; }}
              >
                ⌨️ Dev Tools
              </Link>
              <Link to="/tools/files" style={{
                padding:'11px 24px',
                borderRadius:9,
                background:'rgba(61,214,140,.09)',
                border:'1px solid rgba(61,214,140,.28)',
                color:'#3dd68c',
                fontSize:13.5, fontWeight:700,
                fontFamily:'"Syne",system-ui,sans-serif',
                display:'inline-flex', alignItems:'center', gap:7,
                transition:'all .16s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background='rgba(61,214,140,.17)'; }}
              onMouseLeave={e => { e.currentTarget.style.background='rgba(61,214,140,.09)'; }}
              >
                🗂️ File Tools
              </Link>
            </div>
          </div>

          {/* ── STATS ROW ──────────────────────────────────────────── */}
          <div style={{
            maxWidth:700,
            margin:'0 auto 56px',
            padding:'0 24px',
            display:'grid',
            gridTemplateColumns:'repeat(4,1fr)',
            gap:16,
          }}>
            {/* dividers */}
            <div style={{
              gridColumn:'1/-1',
              height:1,
              background:'linear-gradient(90deg, transparent, rgba(255,255,255,.07), transparent)',
              marginBottom:8,
            }}/>
            {STATS.map((s, i) => (
              <StatItem key={s.label} {...s} index={i}/>
            ))}
            <div style={{
              gridColumn:'1/-1',
              height:1,
              background:'linear-gradient(90deg, transparent, rgba(255,255,255,.07), transparent)',
              marginTop:8,
            }}/>
          </div>

          {/* ── CARDS GRID ─────────────────────────────────────────── */}
          <div style={{
            maxWidth:1100,
            margin:'0 auto',
            padding:'0 24px',
            display:'grid',
            gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))',
            gap:16,
          }}>
            {CATEGORIES.map((cat, i) => (
              <CategoryCard key={cat.id} cat={cat} index={i}/>
            ))}
          </div>

          {/* ── BOTTOM BANNER ──────────────────────────────────────── */}
          <div style={{
            maxWidth:1100,
            margin:'40px auto 0',
            padding:'0 24px',
          }}>
            <div style={{
              padding:'24px 28px',
              borderRadius:14,
              background:'rgba(255,255,255,.02)',
              border:'1px solid rgba(255,255,255,.06)',
              display:'flex',
              alignItems:'center',
              justifyContent:'space-between',
              flexWrap:'wrap',
              gap:16,
            }}>
              <div>
                <div style={{
                  fontSize:15, fontWeight:700,
                  color:'rgba(221,229,245,.85)',
                  fontFamily:'"Syne",system-ui,sans-serif',
                  marginBottom:4,
                }}>
                  Need a custom tool or software for your business?
                </div>
                <div style={{ fontSize:13, color:'rgba(221,229,245,.38)' }}>
                  We build backend systems, AI automation, and cloud infrastructure.
                </div>
              </div>
              <Link to="https://arhatinfo.com/contact" style={{
                padding:'10px 22px',
                borderRadius:9,
                background:'rgba(0,200,240,.1)',
                border:'1px solid rgba(0,200,240,.3)',
                color:'#00c8f0',
                fontSize:13, fontWeight:700,
                fontFamily:'"Syne",system-ui,sans-serif',
                whiteSpace:'nowrap',
                transition:'all .16s',
              }}
              onMouseEnter={e=>{ e.currentTarget.style.background='rgba(0,200,240,.18)'; }}
              onMouseLeave={e=>{ e.currentTarget.style.background='rgba(0,200,240,.1)'; }}
              >
                Talk to us →
              </Link>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}